import { useMemo } from 'react';
import { IVendorPriority, IVendorPriorityGroup } from '../types';
import { VendorType, ShipVendor, PhlebotVendor } from '../enums';

export interface UseVendorPrioritiesResult {
    groupedByVendorType: { [key: string]: IVendorPriorityGroup[] };
    vendorIdsByType: { [key: string]: number[] };
    findPriority: (group: IVendorPriorityGroup, vendorId: number) => IVendorPriority | undefined;
}

export const useVendorPriorities = (priorities: IVendorPriority[]): UseVendorPrioritiesResult => {
    // Group priorities by vendorType
    const groupedByVendorType = useMemo(() => {
        const result: { [key: string]: IVendorPriorityGroup[] } = {};

        priorities.forEach(priority => {
            const vendorTypeKey = VendorType[priority.vendorType] || `Unknown (${priority.vendorType})`;

            if (!result[vendorTypeKey]) {
                result[vendorTypeKey] = [];
            }

            const existingGroup = result[vendorTypeKey].find(g => g.entityType === priority.entityType);

            if (existingGroup) {
                existingGroup.priorities.push(priority);
            } else {
                result[vendorTypeKey].push({
                    vendorType: priority.vendorType,
                    entityType: priority.entityType,
                    priorities: [priority]
                });
            }
        });

        // Sort groups by entity type
        Object.keys(result).forEach(key => {
            result[key].sort((a, b) => a.entityType - b.entityType);
        });

        return result;
    }, [priorities]);

    // Get vendor IDs for each vendor type
    const vendorIdsByType = useMemo(() => {
        const result: { [key: string]: number[] } = {};

        priorities.forEach(priority => {
            const vendorTypeKey = VendorType[priority.vendorType] || `Unknown (${priority.vendorType})`;

            if (!result[vendorTypeKey]) {
                result[vendorTypeKey] = [];
            }

            if (!result[vendorTypeKey].includes(priority.vendorId)) {
                result[vendorTypeKey].push(priority.vendorId);
            }
        });

        // Sort vendor IDs
        Object.keys(result).forEach(key => {
            result[key].sort((a, b) => a - b);
        });

        return result;
    }, [priorities]);

    // Find priority by vendor and entity type
    const findPriority = (group: IVendorPriorityGroup, vendorId: number): IVendorPriority | undefined => {
        return group.priorities.find(p => p.vendorId === vendorId);
    };

    return {
        groupedByVendorType,
        vendorIdsByType,
        findPriority
    };
};